import {
  Connector,
  ConnectorListItem,
  ConnectorCategory,
  ConnectorStatus,
  OAuthInitDto,
  OAuthInitResponse,
  OAuthExchangeDto,
  OAuthExchangeResponse,
} from '@optidev/shared-types';

import { apiClient, createAuthHeaders } from '../api-client';

export interface ConnectorFilters {
  category?: ConnectorCategory;
  status?: ConnectorStatus;
  search?: string;
}

export const connectorsApi = {
  /**
   * Get all connectors with optional filtering
   */
  async getAll(
    token: string,
    filters?: ConnectorFilters,
  ): Promise<ConnectorListItem[]> {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);

    const queryString = params.toString();
    const url = queryString ? `/api/connectors?${queryString}` : '/api/connectors';

    const response = await apiClient.get(url, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Get a single connector by ID
   */
  async getOne(token: string, id: string): Promise<Connector> {
    const response = await apiClient.get(`/api/connectors/${id}`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Get featured connectors
   */
  async getFeatured(token: string): Promise<ConnectorListItem[]> {
    const response = await apiClient.get('/api/connectors/featured', {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Get popular connectors
   */
  async getPopular(token: string): Promise<ConnectorListItem[]> {
    const response = await apiClient.get('/api/connectors/popular', {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Search connectors by query string
   */
  async search(token: string, query: string): Promise<ConnectorListItem[]> {
    const response = await apiClient.get(`/api/connectors/search?q=${encodeURIComponent(query)}`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Initiate OAuth flow for a connector
   */
  async initiateOAuth(
    token: string,
    dto: OAuthInitDto,
  ): Promise<OAuthInitResponse> {
    const response = await apiClient.post('/api/connectors/oauth/initiate', dto, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Exchange OAuth authorization code for tokens
   */
  async exchangeOAuthCode(
    token: string,
    dto: OAuthExchangeDto,
  ): Promise<OAuthExchangeResponse> {
    const response = await apiClient.post('/api/connectors/oauth/exchange', dto, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },
};
